import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';

const ImageToPDF = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    // Clean up preview URLs when component unmounts
    return () => {
      previewUrls.forEach(URL.revokeObjectURL);
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const filesArray = Array.from(files);
      setSelectedFiles(filesArray);
      
      // Create preview URLs for selected images
      const urls = filesArray.map(file => URL.createObjectURL(file));
      setPreviewUrls(prevUrls => {
        prevUrls.forEach(URL.revokeObjectURL);
        return urls;
      });
    }
  };

  const generatePDF = async (): Promise<Blob> => {
    const pdf = new jsPDF();
    let currentPage = 0;

    for (const file of selectedFiles) {
      try {
        // Create a URL for the image file
        const imageUrl = URL.createObjectURL(file);
        
        // Create an image element
        const img = new Image();
        img.src = imageUrl;
        
        await new Promise((resolve) => {
          img.onload = () => {
            // Add a new page if it's not the first image
            if (currentPage > 0) {
              pdf.addPage();
            }

            // Calculate dimensions to fit the image within the PDF page
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            
            const imgRatio = img.width / img.height;
            const pageRatio = pageWidth / pageHeight;
            
            let finalWidth = pageWidth;
            let finalHeight = pageWidth / imgRatio;
            
            if (finalHeight > pageHeight) {
              finalHeight = pageHeight;
              finalWidth = pageHeight * imgRatio;
            }
            
            const xOffset = (pageWidth - finalWidth) / 2;
            const yOffset = (pageHeight - finalHeight) / 2;

            // Add the image to the PDF
            pdf.addImage(img, 'JPEG', xOffset, yOffset, finalWidth, finalHeight);
            
            // Update progress
            currentPage++;
            setProgress((currentPage / selectedFiles.length) * 100);
            
            // Clean up
            URL.revokeObjectURL(imageUrl);
            resolve(null);
          };
        });
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }

    return pdf.output('blob');
  };

  const handlePreview = async () => {
    if (selectedFiles.length === 0) return;

    setConverting(true);
    setProgress(0);

    try {
      const pdfBlob = await generatePDF();
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      const previewUrl = URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(previewUrl);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setConverting(false);
      setProgress(0);
    }
  };

  const handleDownload = async () => {
    if (!pdfPreviewUrl) return;

    const link = document.createElement('a');
    link.href = pdfPreviewUrl;
    link.download = 'converted-images.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
    URL.revokeObjectURL(previewUrls[index]);
    setPreviewUrls(urls => urls.filter((_, i) => i !== index));
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setShowPreview(false);
    }
  };

  return (
    <div className="w-full">
      <h2 className="text-2xl font-montserrat font-semibold mb-4">Image to PDF Converter</h2>
      
      <div className="space-y-6">
        <div className="border-2 border-dashed border-cyan-200 hover:border-cyan-300 transition-colors rounded-xl p-6 text-center bg-gradient-to-b from-cyan-50 to-white">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="file-input"
          />
          <label
            htmlFor="file-input"
            className="cursor-pointer inline-flex items-center justify-center px-6 py-3 bg-cyan-600 text-white rounded-full hover:bg-cyan-700 transition-all transform hover:scale-105 font-semibold shadow-md hover:shadow-lg"
          >
            Select Images
          </label>
          <p className="mt-2 text-sm text-gray-600">
            {selectedFiles.length > 0
              ? `${selectedFiles.length} image${selectedFiles.length > 1 ? 's' : ''} selected`
              : 'Select multiple images to convert to PDF'}
          </p>
        </div>

        {selectedFiles.length > 0 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {previewUrls.map((url, index) => (
                <div key={index} className="group relative">
                  <div className="aspect-square w-full overflow-hidden rounded-lg border border-gray-200 hover:border-cyan-200 transition-colors shadow-sm hover:shadow-md">
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all cursor-pointer hover:bg-red-600 hover:scale-110 shadow-lg text-lg"
                  >
                    ×
                  </button>
                  <p className="mt-2 text-sm text-gray-600 truncate">{selectedFiles[index].name}</p>
                </div>
              ))}
            </div>

            <div className="flex gap-6">
              <button
                onClick={handlePreview}
                disabled={converting}
                className="flex-1 px-6 py-3 bg-cyan-600 text-white rounded-full hover:bg-cyan-700 transition-all transform hover:scale-105 disabled:bg-gray-400 disabled:hover:scale-100 font-semibold shadow-md hover:shadow-lg disabled:shadow-none flex items-center justify-center gap-2"
              >
                {converting ? 'Generating Preview...' : 'Preview PDF'}
              </button>

              {pdfPreviewUrl && (
                <button
                  onClick={handleDownload}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-all transform hover:scale-105 font-semibold shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  Download PDF
                </button>
              )}
            </div>

            {converting && (
              <div className="w-full bg-gray-100 rounded-full h-3 shadow-inner">
                <div
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 h-3 rounded-full transition-all duration-300 shadow-sm"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            )}
          </div>
        )}

        {showPreview && pdfPreviewUrl && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-2">PDF Preview</h3>
            <div className="w-full h-[800px] border border-gray-200 rounded-xl shadow-lg overflow-hidden bg-gray-50">
              <iframe
                src={pdfPreviewUrl}
                className="w-full h-full rounded-xl"
                title="PDF Preview"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageToPDF;
